/**
 * MINDMAP — the RAW-COLOUR / PLAIN-GEOMETRY view of the map, for renderers that
 * cannot take a Tailwind class or an `@xyflow/react` node.
 *
 * There is exactly ONE mindmap in this repo. Its shape comes from
 * {@link layoutMindmap} and its hues from `mindmap-colors.ts` — both of which
 * speak DOM (React Flow nodes, CSS-var strokes, utility classes). React Native
 * can run neither: `react-native-svg` wants a real colour string and an
 * absolutely-positioned `View` wants numbers.
 *
 * This module is the ADAPTER, not a second implementation. It re-projects the
 * same tree and the same palette into hex + plain objects, so a native renderer
 * (the Professor Ka chat card) draws the map flashka-web draws — two-sided fan,
 * identical branch hues, identical tier ramp — and inherits every future change
 * to either instead of drifting away from it.
 *
 * Safe from React Native: `@xyflow/react` is only ever a TYPE import down the
 * chain, so nothing here pulls the DOM canvas into a native bundle.
 */

import {
  BRAND_HEX,
  type BrandPalette,
  type BrandShade,
  mirrorShade,
} from '../ui/styles/brand-colors';
import { isNodeColorKey, type NodeColorKey } from './mindmap-colors';
import { layoutMindmap, type MindmapSide } from './mindmap-layout';
import type { MindmapData, MindmapNode, MindmapNodeSource } from './mindmap-types';

/**
 * The branch hue ORDER, named.
 *
 * `BRANCH_COLORS` has to spell its Tailwind classes out as literals (JIT cannot
 * see `bg-${hue}-100`), which makes the order implicit in eight strings. This
 * array states it once, and `mindmap-native.spec.ts` asserts the two agree —
 * so adding a ninth hue to one table and not the other fails the suite instead
 * of silently giving web and mobile different maps.
 */
export const BRANCH_PALETTES = [
  'sky',
  'orange',
  'emerald',
  'fuchsia',
  'amber',
  'teal',
  'rose',
  'lime',
] as const satisfies readonly BrandPalette[];

/**
 * Every user-pickable node colour is a brand palette of the same name — the
 * palette IS the key. Stated as a type constraint rather than a lookup table so
 * a new `NodeColorKey` that has no ramp behind it fails to compile.
 */
type NodeColorPalette = NodeColorKey & BrandPalette;

/** The shade ramp each visual tier draws from — the numbers in `BRANCH_COLORS`. */
const TIER_SHADES = {
  /** Depth-1 branch heading: tinted fill, stronger border. */
  branch: { bg: '100', border: '300' },
  /** Depth-2+ sub-topic: faint fill, soft border. */
  leaf: { bg: '50', border: '200' },
} as const satisfies Record<string, { bg: BrandShade; border: BrandShade }>;

/** Edge stroke shade — `ROOT_EDGE_COLOR` / `BranchColor.edge` in the class table. */
const EDGE_SHADE: BrandShade = '400';
const ROOT_EDGE_SHADE: BrandShade = '300';
/** `ROOT_NODE_CLASS` — `border-primary-600 bg-primary-500 text-white`. */
const ROOT_BG_SHADE: BrandShade = '500';
const ROOT_BORDER_SHADE: BrandShade = '600';

const hex = (palette: BrandPalette, shade: BrandShade, isDark: boolean): string =>
  BRAND_HEX[palette][isDark ? mirrorShade(shade) : shade];

/** A node's resolved fills and its incoming edge's stroke, as colour strings. */
export type MindmapNodeColors = {
  background: string;
  border: string;
  /** Stroke for the edge arriving at this node (edges take their CHILD's hue). */
  edge: string;
  /** `true` for the filled root, whose label must sit on white. */
  filled: boolean;
};

/**
 * The hue a node actually renders in — its picked colour, else its branch's,
 * else the root's. The precedence is `effectiveColor`'s, kept in step by
 * `mindmap-native.spec.ts`.
 */
export function mindmapNodeColors({
  depth,
  branchIndex,
  color,
  isDark,
}: {
  depth: number;
  /** -1 for the root (as `layoutMindmap` assigns it). */
  branchIndex: number;
  /** The node's user-picked {@link NodeColorKey}, when it has one. */
  color?: string;
  isDark: boolean;
}): MindmapNodeColors {
  const isRoot = depth === 0 || branchIndex < 0;
  const picked: NodeColorPalette | undefined = isNodeColorKey(color) ? color : undefined;

  // A recoloured ROOT keeps the filled look in its new hue (`NodeColor.rootFill`
  // is `border-{p}-500 bg-{p}-400`, except `primary`, which keeps 600/500).
  if (isRoot) {
    const palette: BrandPalette = picked ?? 'primary';
    const bg: BrandShade = picked && picked !== 'primary' ? '400' : ROOT_BG_SHADE;
    const border: BrandShade = picked && picked !== 'primary' ? '500' : ROOT_BORDER_SHADE;
    return {
      background: hex(palette, bg, isDark),
      border: hex(palette, border, isDark),
      edge: hex(palette, ROOT_EDGE_SHADE, isDark),
      filled: true,
    };
  }

  const palette: BrandPalette = picked ?? branchPalette(branchIndex);
  const tier = depth === 1 ? TIER_SHADES.branch : TIER_SHADES.leaf;
  return {
    background: hex(palette, tier.bg, isDark),
    border: hex(palette, tier.border, isDark),
    edge: hex(palette, EDGE_SHADE, isDark),
    filled: false,
  };
}

/** The palette a first-level branch index owns; cycles past the eighth. */
export function branchPalette(index: number): BrandPalette {
  return BRANCH_PALETTES[
    ((index % BRANCH_PALETTES.length) + BRANCH_PALETTES.length) % BRANCH_PALETTES.length
  ];
}

/**
 * Node box geometry, in points.
 *
 * React Native cannot lay an absolutely-positioned tree out from intrinsic
 * widths the way the browser does, so the tiers get FIXED boxes here. The
 * widths are web's `min-w` for the same tiers (`MindmapNode.tsx`: 140 for the
 * root, 120 below it) — the layout's `X_GAP` is spaced for those, so reusing
 * them is what keeps the native map's proportions the web map's.
 */
export const MINDMAP_NATIVE_METRICS = {
  rootWidth: 140,
  nodeWidth: 120,
  rootHeight: 44,
  nodeHeight: 38,
  radius: 8,
  borderWidth: 1,
} as const;

export type MindmapNativeNode = {
  id: string;
  label: string;
  /** Left edge, in layout space (the root sits at x 0). */
  x: number;
  /** Top edge, in layout space. */
  y: number;
  width: number;
  height: number;
  depth: number;
  branchIndex: number;
  side: MindmapSide;
  colors: MindmapNodeColors;
  /** Present when the model grounded this node in the deck's material. */
  source?: MindmapNodeSource;
};

export type MindmapNativeEdge = {
  id: string;
  /** Pre-baked cubic path, in the same layout space as the nodes. */
  d: string;
  stroke: string;
};

export type MindmapNativeLayout = {
  nodes: MindmapNativeNode[];
  edges: MindmapNativeEdge[];
  /** Extent of the laid-out tree, so a caller can fit or scroll it. */
  width: number;
  height: number;
};

/**
 * Lay a logical mindmap out for a native renderer: the SHARED two-sided tidy
 * tree, re-expressed as plain boxes and bezier paths, normalised so the
 * top-left of the content sits at (`padding`, `padding`).
 */
export function layoutMindmapNative(
  data: MindmapData,
  { isDark, padding = 20 }: { isDark: boolean; padding?: number },
): MindmapNativeLayout {
  const { nodes: flowNodes, edges: flowEdges } = layoutMindmap(data);
  if (flowNodes.length === 0) return { nodes: [], edges: [], width: 0, height: 0 };

  // The logical node carries the two things the layout does not thread through
  // its React Flow `data` bag: the user's colour pick and the source grounding.
  const logicalById = new Map<string, MindmapNode>(
    (data?.nodes ?? [])
      .filter((n): n is MindmapNode => !!n && typeof n.id === 'string')
      .map((n) => [n.id, n]),
  );

  const placed: MindmapNativeNode[] = flowNodes.map((n) => {
    const depth = typeof n.data.depth === 'number' ? n.data.depth : 1;
    const branchIndex = typeof n.data.branchIndex === 'number' ? n.data.branchIndex : -1;
    const isRoot = depth === 0 || branchIndex < 0;
    const logical = logicalById.get(n.id);
    return {
      id: n.id,
      label: typeof n.data.label === 'string' ? n.data.label : '',
      x: n.position.x,
      y: n.position.y,
      width: isRoot ? MINDMAP_NATIVE_METRICS.rootWidth : MINDMAP_NATIVE_METRICS.nodeWidth,
      height: isRoot
        ? MINDMAP_NATIVE_METRICS.rootHeight
        : MINDMAP_NATIVE_METRICS.nodeHeight,
      depth,
      branchIndex,
      side: n.data.side === 'left' ? 'left' : 'right',
      colors: mindmapNodeColors({ depth, branchIndex, color: logical?.color, isDark }),
      source: logical?.source,
    };
  });

  // Shift the whole tree so nothing has a negative coordinate — the left-hand
  // branches are laid out at negative x, and a native canvas has no origin to
  // scroll back past.
  const minX = Math.min(...placed.map((n) => n.x));
  const minY = Math.min(...placed.map((n) => n.y));
  const maxX = Math.max(...placed.map((n) => n.x + n.width));
  const maxY = Math.max(...placed.map((n) => n.y + n.height));
  const offX = padding - minX;
  const offY = padding - minY;

  const byId = new Map<string, MindmapNativeNode>();
  for (const n of placed) {
    n.x += offX;
    n.y += offY;
    byId.set(n.id, n);
  }

  // Edges leave the parent on the side its CHILD grew towards and arrive on the
  // child's root-facing side, so a two-sided map's curves never cross the root.
  const edges: MindmapNativeEdge[] = [];
  for (const e of flowEdges) {
    const from = byId.get(e.source);
    const to = byId.get(e.target);
    if (!from || !to) continue;
    const goingLeft = to.side === 'left';
    const sx = goingLeft ? from.x : from.x + from.width;
    const sy = from.y + from.height / 2;
    const tx = goingLeft ? to.x + to.width : to.x;
    const ty = to.y + to.height / 2;
    const mx = (sx + tx) / 2;
    edges.push({
      id: e.id,
      d: `M ${sx} ${sy} C ${mx} ${sy} ${mx} ${ty} ${tx} ${ty}`,
      // The edge takes the hue of the branch it leads INTO — the child's.
      stroke: to.colors.edge,
    });
  }

  return {
    nodes: placed,
    edges,
    width: maxX - minX + padding * 2,
    height: maxY - minY + padding * 2,
  };
}

/** A canvas sheet drawn small: its real geometry, in its real colours. */
export type GraphMiniature = {
  nodes: {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    label: string;
    important: boolean;
    colors: MindmapNodeColors;
  }[];
  edges: { id: string; d: string; stroke: string }[];
  zones: {
    id: string;
    name: string;
    /** The zone name's ink (its badge on the canvas). */
    ink: string;
    x: number;
    y: number;
    width: number;
    height: number;
    fill: string;
    stroke: string;
  }[];
  /** The drawing's box (with padding), in canvas units. */
  minX: number;
  minY: number;
  width: number;
  height: number;
};

/** A node's box on the canvas — the canvas measures it; here it is estimated
 *  from the label the way Tidy up estimates an unmeasured node. */
const miniSize = (label: string, important: boolean) => ({
  width: Math.min(important ? 300 : 260, (important ? 64 : 48) + label.length * 7),
  height: important ? 52 : 40,
});

/**
 * A CANVAS sheet as a miniature — the map as the user arranged it (their
 * positions, loose nodes and zones), which the tree layout above cannot
 * show. Colours follow the canvas: a main node filled, a branch tinted by its
 * hue (or the user's pick), a deeper node fainter; lines take the deeper end's
 * hue and leave a node from the side facing the other, as on the canvas.
 */
export function layoutGraphMiniature(
  graph: {
    nodes: readonly {
      id: string;
      label: string;
      kind: 'important' | 'normal';
      x: number;
      y: number;
      color?: string;
      branch?: number;
    }[];
    edges: readonly { id: string; a: string; b: string }[];
    zones?: readonly {
      id: string;
      name?: string;
      nodeIds: readonly string[];
      color?: string;
    }[];
  },
  { isDark, padding = 24 }: { isDark: boolean; padding?: number },
): GraphMiniature {
  // Depth from the nearest main node: 1 = a branch heading, deeper = fainter.
  const depth = new Map<string, number>();
  const adj = new Map<string, string[]>(graph.nodes.map((n) => [n.id, []]));
  for (const e of graph.edges) {
    adj.get(e.a)?.push(e.b);
    adj.get(e.b)?.push(e.a);
  }
  const queue = graph.nodes.filter((n) => n.kind === 'important').map((n) => n.id);
  for (const id of queue) depth.set(id, 0);
  for (let i = 0; i < queue.length; i++)
    for (const next of adj.get(queue[i]) ?? [])
      if (!depth.has(next)) {
        depth.set(next, depth.get(queue[i])! + 1);
        queue.push(next);
      }

  const nodes = graph.nodes.map((n) => {
    const important = n.kind === 'important';
    return {
      id: n.id,
      x: n.x,
      y: n.y,
      ...miniSize(n.label, important),
      label: n.label,
      important,
      colors: mindmapNodeColors({
        depth: important ? 0 : Math.max(1, depth.get(n.id) ?? 2),
        branchIndex: important ? -1 : Math.max(0, n.branch ?? 0),
        color: n.color,
        isDark,
      }),
    };
  });
  const byId = new Map(nodes.map((n) => [n.id, n]));

  const edges = graph.edges.flatMap((e) => {
    const a = byId.get(e.a);
    const b = byId.get(e.b);
    if (!a || !b) return [];
    const deeper = (depth.get(a.id) ?? 9) >= (depth.get(b.id) ?? 9) ? a : b;
    const [l, r] = a.x <= b.x ? [a, b] : [b, a];
    const apart = r.x - (l.x + l.width) > 0;
    let d: string;
    if (apart) {
      const x1 = l.x + l.width;
      const y1 = l.y + l.height / 2;
      const x2 = r.x;
      const y2 = r.y + r.height / 2;
      const mx = (x1 + x2) / 2;
      d = `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`;
    } else {
      const [t, u] = a.y <= b.y ? [a, b] : [b, a];
      const x1 = t.x + t.width / 2;
      const y1 = t.y + t.height;
      const x2 = u.x + u.width / 2;
      const y2 = u.y;
      const my = (y1 + y2) / 2;
      d = `M${x1},${y1} C${x1},${my} ${x2},${my} ${x2},${y2}`;
    }
    return [{ id: e.id, d, stroke: deeper.colors.edge }];
  });

  const ZONE_PAD = 32;
  const zones = (graph.zones ?? []).flatMap((z) => {
    const members = z.nodeIds.map((id) => byId.get(id)).filter((n) => !!n);
    if (members.length === 0) return [];
    const x = Math.min(...members.map((n) => n.x)) - ZONE_PAD;
    const y = Math.min(...members.map((n) => n.y)) - ZONE_PAD;
    const x2 = Math.max(...members.map((n) => n.x + n.width)) + ZONE_PAD;
    const y2 = Math.max(...members.map((n) => n.y + n.height)) + ZONE_PAD;
    // A zone is an AREA, not a node: the canvas's own faint `-50` fill and
    // `-200` border, which the miniature draws see-through and dashed, with
    // the zone's name — so it never reads as one big node.
    const palette: NodeColorPalette = isNodeColorKey(z.color) ? z.color : 'amber';
    return [
      {
        id: z.id,
        name: z.name ?? '',
        ink: hex(palette, '600', isDark),
        x,
        y,
        width: x2 - x,
        height: y2 - y,
        fill: hex(palette, '50', isDark),
        stroke: hex(palette, '300', isDark),
      },
    ];
  });

  const boxes = [...nodes, ...zones];
  if (boxes.length === 0)
    return { nodes, edges, zones, minX: 0, minY: 0, width: 0, height: 0 };
  const minX = Math.min(...boxes.map((b) => b.x)) - padding;
  const minY = Math.min(...boxes.map((b) => b.y)) - padding;
  const maxX = Math.max(...boxes.map((b) => b.x + b.width)) + padding;
  const maxY = Math.max(...boxes.map((b) => b.y + b.height)) + padding;
  return { nodes, edges, zones, minX, minY, width: maxX - minX, height: maxY - minY };
}
